import { Action, Events, ResponseAction, Source, Status, Target } from '../types';
import { GzLinkWebComponent } from '../../../ui/gzLinkComponent';
import { Target as LinkTarget } from '../../../ui/gzLinkComponent/types';
import { dateFormatter } from '@nirvana/shared/time';
import { SelectEvents, SelectOption } from '../../../ui/gzSelect/types';
import { isStatusTransitionValid } from '../utils';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { Feature, Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';
import { buildHash } from '@nirvana/shared/navigation';
import { ColumnKeys as DevicesColumnKeys } from '../../../riskManagement/devicesApp/types';

export function objectRenderer({ item }: { item: ResponseAction }): string | GzLinkWebComponent {
  return Target.ENDPOINT === item.targetType && this.dependencies.userHasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])
    ? buildLink(item.objectName, (): void => {
        this.dependencies.openEndpointDetailsGeneralWindow(item.endpointId);
      })
    : item.objectName;
}

export function detailsRenderer({ item }: { item: ResponseAction }): GzLinkWebComponent | string {
  if (item.errorId) {
    return this.stringsTranslator.translateEdrResponseActionsError(
      item.errorId,
      item.errorDescription as string | null
    );
  } else if (Status.EXECUTION_SUCCESS !== item.status) {
    return '-';
  }

  let actionDetails;

  switch (item.type) {
    case Action.ADD_TO_QUARANTINE:
      actionDetails = getAddToQuarantineDetails.call(this, item);

      break;

    case Action.ADD_TO_SANDBOX:
      actionDetails = getAddToSandboxDetails.call(this, item);

      break;

    case Action.COLLECT_INVESTIGATION_PACKAGE:
      actionDetails = getCollectInvestigationPackageDetails.call(this, item);

      break;

    case Action.SCAN:
      actionDetails = getScanDetails.call(this, item);

      break;

    case Action.RISK_SCAN:
      actionDetails = getRiskScanDetails.call(this, item);

      break;
  }

  return actionDetails ?? '-';
}

function getRiskScanDetails(responseAction: ResponseAction): GzLinkWebComponent | null {
  if (!this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_IOR_VISIBLE)) {
    return null;
  }

  const params = {
    filters: [
      {
        [DevicesColumnKeys.DEVICE_NAME]: {
          complexEnumFilter: [{
            label: responseAction.objectName,
            value: responseAction.endpointId,
            type: responseAction.targetType,
          }],
        },
      },
      {
        [DevicesColumnKeys.COMPANY]: {
          exactMatch: responseAction.companyId
        },
      },
    ],
  };

  const url = buildHash('/risk-management-resources', params);

  return buildLink(translateFn('View resources'), url);
}

function getAddToQuarantineDetails(responseAction: ResponseAction): GzLinkWebComponent | null {
  return responseAction.executionDetails?.endpointName &&
    this.dependencies.userHasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])
    ? buildLink(translateFn('Quarantine item'), (): void => {
        this.dependencies.navigateToQuarantineItem(
          responseAction.executionDetails?.endpointName as string,
          responseAction.objectName,
          responseAction.companyId
        );
      })
    : null;
}

function getAddToSandboxDetails(responseAction: ResponseAction): GzLinkWebComponent | null {
  return responseAction.executionDetails?.reportId
    ? buildLink(translateFn('Show report'), (): void => {
        this.dependencies.showSandboxReport(responseAction.executionDetails?.reportId as string);
      })
    : null;
}

function getCollectInvestigationPackageDetails(responseAction: ResponseAction): GzLinkWebComponent | string | null {
  if (
    !this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_INVESTIGATION_TOOLS_VISIBLE) ||
    !this.dependencies.userHasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])
  ) {
    return null;
  } else if (!this.dependencies.isSecureLogin()) {
    return translateFn('Collected investigation packages cannot be accessed by users without 2FA');
  }

  return buildLink(translateFn('Investigation files'), (): void => {
    this.dependencies.openEndpointDetailsInvestigationWindow(responseAction.endpointId);
  });
}

function getScanDetails(responseAction: ResponseAction): GzLinkWebComponent | null {
  return this.dependencies.userHasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])
    ? buildLink(translateFn('Scan logs'), (): void => {
        this.dependencies.openEndpointDetailsScanLogsWindow(responseAction.endpointId);
      })
    : null;
}

export function updateStatusActionsRenderer({
  item: { _id, status },
}: {
  item: { _id: string; status: Status };
}): GzSelectWebComponent | null {
  const availableActions = getAvailableUpdateStatusActions(this.allUpdateStatusActions, status);

  if (0 === availableActions.length) {
    return null;
  }

  const select = new GzSelectWebComponent();

  select.options = availableActions;
  select.classList.add('d-flex', 'align-items-center', 'h-full');
  select.innerHTML = `<gz-icon-button
    icon="ds-icon-more"
    size="24"
    fontSize="22"
    buttonType="square"
    buttonFill="quiet"
    buttonColor="secondary"
    class="inline-menu-trigger"
  />`;

  select.addEventListener(SelectEvents.SELECTION_CHANGED, (event: WebComponentEvent): void => {
    this.dom.$gridView.dispatchEvent(
      new CustomEvent(Events.UPDATE_STATUS, { detail: { id: _id, status: Number(event.detail.selected) } })
    );
  });

  return select;
}

function getAvailableUpdateStatusActions(allActions: Array<SelectOption>, fromStatus: Status): Array<SelectOption> {
  return allActions.filter((action: SelectOption): boolean => {
    const toStatus = Number(action.value) as Status;

    return isStatusTransitionValid(fromStatus, toStatus);
  });
}

export function executedOnRenderer({ item: { executedOn } }: { item: { executedOn?: string } }): string {
  return executedOn ? dateFormatter(executedOn, '') : '-';
}

export function executedByRenderer({
  item: { source, sourceDetails },
}: {
  item: { source: Source; sourceDetails?: { id?: string; name: string } };
}): string | GzLinkWebComponent {
  if (!sourceDetails) {
    return '-';
  } else if (
    Source.MANUAL === source ||
    (Source.CUSTOM_RULE === source &&
      (!sourceDetails.id ||
        !(
          this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_EDR_CUSTOM_RULES) &&
          this.dependencies.userHasAllRights([Rights.RIGHT_MANAGE_INVENTORY, Rights.MANAGE_POLICIES_READ])
        )))
  ) {
    return sourceDetails.name;
  }

  return buildLink(sourceDetails.name, (): void => {
    this.dom.$gridView.dispatchEvent(
      new CustomEvent(Events.OPEN_CUSTOM_RULE_DETAILS_PANEL, { detail: { id: sourceDetails.id } })
    );
  });
}

function buildLink(innerText: string, callback: EventListenerOrEventListenerObject | string): GzLinkWebComponent {
  const link = new GzLinkWebComponent();

  link.innerText = innerText;
  if (typeof callback === 'string') {
    link.target = LinkTarget.SELF;
    link.href = callback;
  } else {
    link.callback = callback;
  }

  return link;
}
